import { router } from "@inertiajs/react";
import { ActionIcon, Autocomplete, Button, Menu, Select, Stack, Text, Tooltip } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconFileDescription, IconFileIsr, IconUser, IconUsers } from "@tabler/icons-react";
import type { DataTableSortStatus } from "mantine-datatable";
import { type ReactNode, useState } from "react";
import { FilterSection } from "../../components/filter/FilterSection";
import { ReportGeneratorModal } from "../../components/modals/ReportGeneratorModal";
import { SingleReportGeneratorModal } from "../../components/modals/SingleReportGeneratorModal";
import { ReportTable } from "../../components/report/ReportTable";
import { FilterSelect } from "../../components/select/FilterSelect";
import { showDeleteModal } from "../../helpers/modal";
import { useAdmin } from "../../hooks/useProps";
import { useYearContext } from "../../hooks/useYearContext";
import { ContentShell } from "../../layout/ContentShell";
import type { Apprentice } from "../../models/apprentice";
import { getOccupationOptions, type Occupation } from "../../models/enums/occupation";
import { editReportTypeRoute, getReportTypeOptions, type ReportType } from "../../models/enums/reportType";
import { type Group, getGroupOptions } from "../../models/group";
import type { Pagination } from "../../models/pagination";
import type { Report } from "../../models/reports/report";
import { generate_document_report_path, generate_documents_reports_path, report_path, reports_path } from "../../routes";

type ReportsProps = {
  reports: Report[];
  apprentices: Apprentice[];
  apprentice_names: string[];
  apprentice_shorts: string[];
  company_names: string[];
  supervisor_names: string[];
  groups: Group[];

  filter: FilterFormType & {
    sort_key?: "name";
    sort_direction?: "asc" | "desc";
  };
  pagination: Pagination;
};

type FilterFormType = {
  title?: string;
  apprentice_name?: string;
  apprentice_short?: string;
  company_name?: string;
  supervisor_name?: string;
  report_type?: ReportType;
  occupation?: Occupation;
  group_id?: number;
};

const resetFilters = {
  title: undefined,
  apprentice_name: undefined,
  apprentice_short: undefined,
  company_name: undefined,
  supervisor_name: undefined,
  report_type: undefined,
  occupation: undefined,
  group_id: undefined,
};

export default function Reports({
  reports,
  apprentice_names,
  apprentice_shorts,
  company_names,
  supervisor_names,
  apprentices,
  filter,
  groups,
  pagination,
}: ReportsProps) {
  const [openedGenerator, { open: openGenerator, close: closeGenerator }] = useDisclosure(false);
  const [openedSingleGenerator, { open: openSingleGenerator, close: closeSingleGenerator }] = useDisclosure(false);
  const { pathWithYearContext } = useYearContext();
  const { sort_key, sort_direction, ...filterFormData } = filter;
  const sortStatus: DataTableSortStatus<Report> = {
    columnAccessor: sort_key || "",
    direction: sort_direction || "asc",
  };
  const [selectedRecords, setSelectedRecord] = useState<Report[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  const isAdmin = useAdmin();

  const filterForm = useForm<FilterFormType>({
    mode: "uncontrolled",
    initialValues: filterFormData,
  });

  const onEdit = (report: Report) =>
    router.get(pathWithYearContext(editReportTypeRoute[report.report_configuration.report_type], report.id));

  const onDelete = (report: Report) =>
    showDeleteModal({
      title: `Bericht ${report.title}`,
      article: "den",
      onConfirm: () => router.delete(pathWithYearContext(report_path, report.id)),
    });

  const onSortChange = (sort: DataTableSortStatus<Report>) => handlePageUpdate(filterFormData, sort, pagination.page);
  const onFilterSubmit = () => handlePageUpdate(filterForm.getValues(), sortStatus, pagination.page);
  const onReset = () => handlePageUpdate(resetFilters, sortStatus, pagination.page);
  const onPageChange = (page: number) => handlePageUpdate(filterFormData, sortStatus, page);

  const handlePageUpdate = (filters: FilterFormType, sortStatus: DataTableSortStatus<Report>, page: number) => {
    setIsFetching(true);
    router.visit(pathWithYearContext(reports_path), {
      only: ["reports", "filter", "pagination"],
      data: {
        filter: {
          ...filters,
          sort_key: sortStatus.columnAccessor,
          sort_direction: sortStatus.direction,
        },
        page: page,
      },
      onFinish: () => setIsFetching(false),
    });
  };

  const onCreateGenerate = () =>
    modals.openConfirmModal({
      title: "Dokumente generieren",
      children: (
        <Text>{`Möchten Sie wirklich ${selectedRecords.length > 1 ? `die ${selectedRecords.length} Dokumente` : "das Dokument"} generieren`}</Text>
      ),
      labels: { confirm: "Generieren", cancel: "Abbrechen" },
      confirmProps: { color: "zli-green" },
      onConfirm: () => {
        router.post(pathWithYearContext(generate_documents_reports_path), { report_ids: selectedRecords.map((s) => s.id) });
        setSelectedRecord([]);
      },
    });

  const onRender = (report: Report) => router.post(pathWithYearContext(generate_document_report_path, report.id));

  const renderAdditionalActions = (row: Report) => (
    <Tooltip label="Dokument generieren">
      <ActionIcon size="md" variant="subtle" onClick={() => onRender(row)}>
        <IconFileIsr />
      </ActionIcon>
    </Tooltip>
  );

  const actionButtons: ReactNode = (
    <>
      {isAdmin && (
        <Menu width={180}>
          <Menu.Target>
            <Button leftSection={<IconFileDescription />} variant="light">
              Berichte erstellen
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconUser />} onClick={() => openSingleGenerator()}>
              Lernende/r
            </Menu.Item>
            <Menu.Item leftSection={<IconUsers />} onClick={() => openGenerator()}>
              Gruppe
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      )}
      <Button
        disabled={selectedRecords.length === 0}
        leftSection={<IconFileIsr />}
        color="zli-green"
        variant="light"
        onClick={onCreateGenerate}
      >
        Dokumente generieren
      </Button>
    </>
  );

  return (
    <ContentShell title="Berichte" actions={actionButtons}>
      <SingleReportGeneratorModal opened={openedSingleGenerator} close={closeSingleGenerator} apprentices={apprentices} />
      <ReportGeneratorModal opened={openedGenerator} close={closeGenerator} groups={groups} />
      <Stack>
        <FilterSection
          form={filterForm}
          onSubmit={onFilterSubmit}
          onReset={onReset}
          filters={[
            <Autocomplete
              label="Name"
              clearable
              data={apprentice_names}
              key={filterForm.key("apprentice_name")}
              {...filterForm.getInputProps("apprentice_name")}
            />,
            <Autocomplete
              label="Kürzel"
              clearable
              data={apprentice_shorts}
              key={filterForm.key("apprentice_short")}
              {...filterForm.getInputProps("apprentice_short")}
            />,
            <Autocomplete
              label="Lehrbetrieb"
              clearable
              data={company_names}
              key={filterForm.key("company_name")}
              {...filterForm.getInputProps("company_name")}
            />,
            <Autocomplete
              label="Berufsbildner/in"
              clearable
              data={supervisor_names}
              key={filterForm.key("supervisor_name")}
              {...filterForm.getInputProps("supervisor_name")}
            />,
            <Select
              label="Beruf"
              clearable
              data={getOccupationOptions()}
              key={filterForm.key("occupation")}
              {...filterForm.getInputProps("occupation")}
            />,
            <FilterSelect
              label="Gruppe"
              clearable
              data={getGroupOptions(groups, false)}
              key={filterForm.key("group_id")}
              {...filterForm.getInputProps("group_id")}
            />,
            <Select
              label="Bericht"
              clearable
              data={getReportTypeOptions()}
              key={filterForm.key("report_type")}
              {...filterForm.getInputProps("report_type")}
            />,
          ]}
        />
        <ReportTable
          reports={reports}
          onEdit={onEdit}
          onDelete={onDelete}
          sortStatus={sortStatus}
          onSortStatusChange={onSortChange}
          renderAdditionalActions={renderAdditionalActions}
          selectedRecords={selectedRecords}
          onSelectedRecordsChange={setSelectedRecord}
          pagination={pagination}
          onPageChange={onPageChange}
          fetching={isFetching}
        />
      </Stack>
    </ContentShell>
  );
}
